import { useEffect, useState } from "react";
import { useNavigate, Outlet, useLocation } from "react-router-dom";
import { HiOutlineMenu, HiOutlineX, HiOutlineHome, HiOutlineLogout } from "react-icons/hi";
import { MdOutlineAssessment } from "react-icons/md";
import { PiBowlFood } from "react-icons/pi";
import { RiLayoutGridLine, RiListCheck, RiTimeLine } from 'react-icons/ri';
import { useOutlet } from "../../../context/OutletContext";
import VoiceSettings from "./VoiceSettings"; // adjust path as needed

const NAV_ITEMS = [
    { label: "Home", icon: HiOutlineHome, to: "/home" },
    { label: "Order Report", icon: MdOutlineAssessment, to: "/report" },
    { label: "Item Report", icon: PiBowlFood, to: "/items-report" },
];

const DENSITY_OPTIONS = [
    { key: "comfortable", icon: RiListCheck, label: "Comfortable" },
    { key: "dense", icon: RiLayoutGridLine, label: "Dense" },
    { key: "timeline", icon: RiTimeLine, label: "Timeline" },
];

const KitchenLayout = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [density, setDensity] = useState(() => {
        return localStorage.getItem("kds-density") || "comfortable";
    });
    const navigate = useNavigate();
    const location = useLocation();

    const { defaultOutlet } = useOutlet();

    const isHome = location.pathname === "/home";

    const logoutUser = () => {
        localStorage.clear();
        navigate("/");
    };

    const handleNavigation = (to) => {
        if (to === "/home") setDensity("comfortable");
        if (location.pathname === to) return;
        window.open(to, "_blank", "noopener,noreferrer");
    };

    useEffect(() => {
        localStorage.setItem("kds-density", density);
    }, [density]);

    const DensityToggle = ({ collapsed = false }) => (
        <div className={`flex ${collapsed ? "flex-col items-center gap-1" : "flex-col gap-1 px-2 pb-3"}`}>
            {!collapsed && (
                <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 px-1 mb-0.5 pt-3">
                    Density
                </span>
            )}
            {DENSITY_OPTIONS.map(({ key, icon: Icon, label }) => (
                <button
                    key={key}
                    onClick={() => { setDensity(key); setSidebarOpen(false); }}
                    title={collapsed ? label : undefined}
                    className={`flex items-center transition-colors rounded-lg text-sm font-medium
                        ${collapsed ? "w-9 h-9 justify-center" : "gap-2.5 px-3 py-2 w-full"}
                        ${density === key
                            ? "bg-violet-50 text-violet-600"
                            : "text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                        }`}
                >
                    <Icon size={17} className="shrink-0" />
                    {!collapsed && <span>{label}</span>}
                </button>
            ))}
        </div>
    );

    return (
        <div className="flex min-h-screen bg-gray-50">

            {/* Backdrop */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/30 z-40"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* ── Collapsed icon rail ─────────────────────────────────────────── */}
            <div className={`fixed top-0 left-0 h-screen w-14 bg-white border-r border-gray-200 flex flex-col items-center py-3 gap-1 z-50 shadow-sm transition-opacity duration-200 ${sidebarOpen ? "opacity-0 pointer-events-none" : "opacity-100"}`}>
                <button
                    onClick={() => setSidebarOpen(true)}
                    className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition-colors mb-2"
                    title="Open menu"
                >
                    <HiOutlineMenu size={18} />
                </button>

                <div className="w-6 border-t border-gray-200 mb-1" />

                {NAV_ITEMS.map(({ label, icon: Icon, to }) => (
                    <button
                        key={label}
                        title={label}
                        onClick={() => handleNavigation(to)}
                        className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors
                            ${location.pathname === to
                                ? "bg-blue-50 text-blue-600"
                                : "hover:bg-gray-100 text-gray-500 hover:text-gray-800"
                            }`}
                    >
                        <Icon size={18} />
                    </button>
                ))}

                {/* Density toggle */}
                {isHome && (
                    <>
                        <div className="w-6 border-t border-gray-200 my-1" />
                        <DensityToggle collapsed />
                    </>
                )}

                {/* Voice settings — always visible in rail, panel opens to the right */}
                <div className="w-6 border-t border-gray-200 my-1" />
                <VoiceSettings collapsed />

                <div className="mt-auto">
                    <button
                        onClick={logoutUser}
                        className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                        title="Logout"
                    >
                        <HiOutlineLogout size={18} />
                    </button>
                </div>
            </div>

            {/* ── Expanded sidebar ────────────────────────────────────────────── */}
            <div className={`fixed top-0 left-0 h-screen w-64 bg-white border-r border-gray-200 shadow-xl z-50 flex flex-col transition-transform duration-200 ease-out ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                    <div className="space-y-1.5">
                        <div className="font-semibold text-gray-800 text-sm leading-tight">Menu</div>
                        <div className="text-sm text-violet-500 font-semibold truncate max-w-[160px]">{defaultOutlet}</div>
                    </div>
                    <button
                        onClick={() => setSidebarOpen(false)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
                    >
                        <HiOutlineX size={18} />
                    </button>
                </div>

                <div className="flex-1 px-2 py-2 flex flex-col overflow-y-auto">
                    {/* Nav links */}
                    <nav className="flex flex-col gap-0.5">
                        {NAV_ITEMS.map(({ label, icon: Icon, to }) => (
                            <button
                                key={label}
                                onClick={() => { handleNavigation(to); setSidebarOpen(false); }}
                                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors w-full text-left
                                    ${location.pathname === to
                                        ? "bg-blue-50 text-blue-600 font-medium"
                                        : "hover:bg-gray-100 text-gray-700 hover:text-gray-900"
                                    }`}
                            >
                                <Icon size={17} className={`shrink-0 ${location.pathname === to ? "text-blue-600" : "text-gray-500"}`} />
                                <span>{label}</span>
                            </button>
                        ))}
                    </nav>

                    {/* Density toggle */}
                    {isHome && (
                        <div className="border-t border-gray-200">
                            <DensityToggle collapsed={false} />
                        </div>
                    )}

                    {/* Voice settings — always visible in expanded sidebar */}
                    <div className="border-t border-gray-200 px-2 py-3">
                        <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 px-1 block mb-1">
                            Audio
                        </span>
                        <VoiceSettings collapsed={false} />
                    </div>
                </div>

                <div className="px-2 py-3 border-t border-gray-200">
                    <button
                        onClick={logoutUser}
                        className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-red-50 text-gray-500 hover:text-red-600 text-sm transition-colors w-full text-left"
                    >
                        <HiOutlineLogout size={17} className="shrink-0" />
                        <span>Logout</span>
                    </button>
                </div>
            </div>

            {/* Main content */}
            <div className="flex-1 pl-14 min-w-0 flex flex-col min-h-screen">
                <main className="flex-1">
                    <Outlet context={{ density }} />
                </main>

                {/* <footer className="border-t border-gray-200 bg-white px-4 py-2.5">
                    <div className="flex items-center justify-center font-semibold text-sm text-[#55554d]">
                        © {new Date().getFullYear()} SilverlinePOS System Integrators. All rights reserved.
                    </div>
                </footer> */}
            </div>

        </div>
    );
};

export default KitchenLayout;